import React from "react";
import { Plus, X } from "lucide-react";

import { ADDED_HEADING, CHANGE_NOTICE_HEADING, EMPTY_SECTION_FALLBACK, REMOVED_HEADING, REMOVED_LEAD_DELETED, addedLead } from "../changeNoticeCopy";

/**
 * What the candidate is told after a compliance officer edits the checklist
 * they are part-way through.
 *
 * Two independent groups under one neutral heading, rather than one sentence
 * describing one case. The page used to carry a single amber box reading "The
 * requirements changed — one document is no longer needed", which could only
 * describe REMOVALS. A removal is the half of a checklist edit that asks
 * nothing of the candidate; an ADDED document is the half they have to act on,
 * and the page never mentioned one.
 *
 * Structured as groups for the same reason the officer-side ChangeList is: one
 * edit can add, remove, or do both, and a single sentence cannot open with
 * "here is what to do" and "here is what to stop worrying about" at once.
 *
 * Added comes FIRST and reads as an instruction. It is the only part with an
 * action attached, and burying it under reassurance is how a candidate ends up
 * thinking a changed checklist needs nothing from them.
 *
 * Each document is named individually and carries its section — the same
 * "Government Documents › Pan Card" shape the officer's history modal shows.
 * Naming them is not decoration: a list that is quietly longer or shorter than
 * last time reads as the site having lost an upload, and "some documents"
 * invites the candidate to go checking which.
 */
const ChecklistChangeNotice = ({ addedDocuments, removedDocuments }) => {
  const added = Array.isArray(addedDocuments) ? addedDocuments : [];
  const removed = Array.isArray(removedDocuments) ? removedDocuments : [];

  if (added.length === 0 && removed.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-border-primary/60 bg-background-form/30 px-3.5 py-3">
      <p className="text-sm font-medium text-text-primary">{CHANGE_NOTICE_HEADING}</p>

      <div className="mt-2.5 flex flex-col gap-3">
        {added.length > 0 && (
          <div>
            <p className="text-[11.5px] font-semibold tracking-wide text-tone-emerald">{ADDED_HEADING}</p>
            <p className="mt-0.5 text-[13px] text-text-secondary">{addedLead(added.length)}</p>
            <ul className="mt-1.5 space-y-1">
              {added.map((doc, i) => (
                <li key={`${doc.name}-${i}`} className="flex items-start gap-1.5 text-[13px] text-text-secondary">
                  <Plus size={12} className="mt-1 shrink-0 text-tone-emerald" />
                  <span className="min-w-0">
                    <span className="text-text-primary/70">{doc.sectionName || EMPTY_SECTION_FALLBACK} › </span>
                    <span className="font-medium text-text-primary">{doc.name}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {removed.length > 0 && (
          <div>
            <p className="text-[11.5px] font-semibold tracking-wide text-tone-rose">{REMOVED_HEADING}</p>
            {/* ⚠️ This sentence claims the files are deleted. They are not —
                see changeNoticeCopy.js, which carries the full note and what
                it would take to make it true. */}
            <p className="mt-0.5 text-[13px] text-text-secondary">{REMOVED_LEAD_DELETED}</p>
            <ul className="mt-1.5 space-y-1">
              {removed.map((doc, i) => (
                <li key={`${doc.name}-${i}`} className="flex items-start gap-1.5 text-[13px] text-text-secondary">
                  <X size={12} className="mt-1 shrink-0 text-tone-rose" />
                  <span className="min-w-0">
                    {doc.sectionName && <span className="text-text-primary/70">{doc.sectionName} › </span>}
                    <span className="font-medium text-text-primary">{doc.name}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistChangeNotice;
